/**
 * Change history for the site-configuration store.
 *
 * Mirrors services/history.ts exactly — same design, same tradeoffs (a bulk
 * import-style operation, if this store ever gets one, logs as a single
 * non-restorable line rather than per-entity entries). Kept as its own module
 * rather than shared, since it is typed against SiteConfigStore/
 * SiteConfigEntityKind and the two models otherwise have nothing in common.
 */

import { ulid } from "ulid"
import { type SiteConfigStore, type SiteConfigEntityKind, SITECONFIG_BUCKET, siteConfigDisplayName } from "./types"
import { pushHistory } from "../shared/persistence/historySync"

// No "import" action: unlike the services backend there is no bulk-import
// path into site config, so nothing can ever produce one.
export type ChangeAction = "create" | "update" | "delete" | "translate" | "revert"

export interface ChangeEntry {
  id: string
  at: number
  action: ChangeAction
  /** Whether the change came from the agent or a person editing directly. */
  actor: "agent" | "user"
  /** Attribution. For agent changes this is the signed-in user who prompted it. */
  authorName?: string
  authorEmail?: string
  authorId?: string
  /** Model id, when the agent made the change. */
  viaModel?: string
  /** Absent for a bulk entry with no single entity to restore. */
  entityKind?: SiteConfigEntityKind
  entityId?: string
  /** Human-readable name at the time of the change. */
  entityName: string
  locale?: string
  /** One-line description shown in the log. */
  summary: string
  /** The entity as it was before this change. Absent for create (nothing existed). */
  before?: Record<string, any>
  /** The entity as it was after this change. Absent for delete. */
  after?: Record<string, any>
  /** True once this entry has been reverted, so the UI can grey it out. */
  reverted?: boolean
}

const HISTORY_KEY_PREFIX = "svc-siteconfig-history"
const DEFAULT_DB = "default"
const MAX_ENTRIES = 200

/** dbId matches the id passed to store.ts's loadStore/saveStore, so a team's history stays with its data. */
function historyKeyFor(dbId: string) { return dbId === DEFAULT_DB ? HISTORY_KEY_PREFIX : `${HISTORY_KEY_PREFIX}:${dbId}` }

/*
 * In-memory cache backing the synchronous history API.
 *
 * Entries are written to Postgres (see shared/persistence/historySync) and
 * read back on load; this map is what the synchronous readers see in between.
 * Not localStorage -- a change log that only the browser which made the
 * change can read is not an audit trail.
 */
const cache = new Map<string, ChangeEntry[]>()

export function loadHistory(dbId = DEFAULT_DB): ChangeEntry[] {
  return cache.get(historyKeyFor(dbId)) ?? []
}

export function saveHistory(entries: ChangeEntry[], dbId = DEFAULT_DB): void {
  pushHistory("site-config", entries)
  cache.set(historyKeyFor(dbId), entries.slice(-MAX_ENTRIES))
}

export function clearHistory(dbId = DEFAULT_DB): void {
  cache.delete(historyKeyFor(dbId))
}

export function makeEntry(args: Omit<ChangeEntry, "id" | "at">): ChangeEntry {
  return { ...args, id: ulid(), at: Date.now() }
}

function putInBucket(store: SiteConfigStore, kind: SiteConfigEntityKind, entity: Record<string, any>): void {
  ;(store[SITECONFIG_BUCKET[kind]] as Record<string, any>)[entity.id] = entity
}

function dropFromBucket(store: SiteConfigStore, kind: SiteConfigEntityKind, id: string): void {
  delete (store[SITECONFIG_BUCKET[kind]] as Record<string, any>)[id]
}

/**
 * Applies a revert: puts the recorded `before` state back (or removes the
 * entity, for undoing a create). Returns the new store and a description, or
 * an error string when the entry can't be reverted.
 */
export function revertEntry(
  store: SiteConfigStore,
  entry: ChangeEntry
): { store: SiteConfigStore; description: string } | { error: string } {
  if (entry.reverted) return { error: "That change has already been reverted." }
  if (!entry.entityKind || !entry.entityId) {
    return { error: "This entry covers a bulk operation, not one entity, so it can't be reverted as a single step." }
  }

  const next: SiteConfigStore = JSON.parse(JSON.stringify(store))

  if (entry.action === "create") {
    dropFromBucket(next, entry.entityKind, entry.entityId)
    return { store: next, description: `Removed "${entry.entityName}" (undo of create)` }
  }

  if (!entry.before) {
    return { error: "This entry has no saved snapshot, so it can't be reverted." }
  }

  if (entry.action === "delete") {
    putInBucket(next, entry.entityKind, entry.before)
    return { store: next, description: `Restored "${entry.entityName}"` }
  }

  // update / translate: restore the prior state.
  putInBucket(next, entry.entityKind, entry.before)
  return { store: next, description: `Reverted ${entry.action} on "${entry.entityName}"` }
}

/** Version id for the state recorded BEFORE an entry, distinct from its after-state. */
export const priorVersionId = (entryId: string) => `${entryId}:before`

/**
 * The version timeline for one entity: every recorded state of it, newest
 * first. Each version is the `after` snapshot of a change (or the `before`
 * snapshot of the earliest recorded change, for the state that preceded it).
 */
export interface EntityVersion {
  /**
   * Identifies this VERSION, not the entry it came from — one entry can yield
   * two versions (its `after` state, and its `before` state when it is the
   * earliest recorded change), and those must be separately addressable or
   * the prior state can never be restored. The prior-state row is suffixed
   * `:before`; use `sourceEntryId` for the raw entry id.
   */
  entryId: string
  /** The history entry this version was derived from. */
  sourceEntryId: string
  at: number
  action: ChangeAction
  author: string
  actor: "agent" | "user"
  summary: string
  /** The entity as it was at this point. Absent when the snapshot was trimmed. */
  entity?: Record<string, any>
  /** True for the state BEFORE the entry, rather than after it. */
  isPriorState?: boolean
}

export function versionsForEntity(entries: ChangeEntry[], entityId: string): EntityVersion[] {
  // Exclude BOTH the synthetic "revert" log lines (which carry no snapshot of
  // their own worth restoring) and entries that have since been reverted — an
  // undone change's snapshot is no longer a state this entity was ever left in.
  const relevant = entries.filter(e => e.entityId === entityId && e.action !== "revert" && !e.reverted)
  const out: EntityVersion[] = []

  for (const e of relevant) {
    const author = e.authorName || e.authorEmail || (e.actor === "agent" ? "agent" : "unknown")
    if (e.after) {
      out.push({
        entryId: e.id, sourceEntryId: e.id, at: e.at, action: e.action,
        author, actor: e.actor, summary: e.summary, entity: e.after,
      })
    }
  }

  // Prepend the state before the earliest change, so the original is restorable.
  // Its id must differ from that entry's after-state id, or lookups by id
  // resolve to the wrong version and the original becomes unreachable.
  const earliest = relevant[0]
  if (earliest?.before) {
    const author = earliest.authorName || earliest.authorEmail || (earliest.actor === "agent" ? "agent" : "unknown")
    out.unshift({
      entryId: priorVersionId(earliest.id), sourceEntryId: earliest.id,
      at: earliest.at, action: earliest.action, author,
      actor: earliest.actor, summary: `State before: ${earliest.summary}`,
      entity: earliest.before, isPriorState: true,
    })
  }

  return out.reverse() // newest first
}

/** Writes one recorded version of an entity back into the store. */
export function restoreEntityVersion(store: SiteConfigStore, kind: SiteConfigEntityKind, entity: Record<string, any>): SiteConfigStore {
  const next: SiteConfigStore = JSON.parse(JSON.stringify(store))
  putInBucket(next, kind, entity)
  return next
}
